<template>
  <label :class="classesWrapperInput">
    <span class="text-field__label" v-if="!!label">{{label}}</span>
    <div :class="classesInput">
      <input
        class="user-click"
        :style="{'color': colorText}"
        :value="internalValue"
        :placeholder="placeholder"
        v-bind="$attrs"
        :maxlength="maxlength"
        v-on="{
          ...$listeners,
          input: event => $emit('input', event.target.value)
        }"
      >
      <span
        v-if="!!appendInnerIcon"
        class="text-field__input__icon text-field__input__icon--prepend-inner"
      >
        <app-icon :name="prependInnerIcon" size="xs" color="gray-light"/>
      </span>
      <span
        v-if="!!prependInnerIcon"
        class="text-field__input__icon text-field__input__icon--prepend-inner"
      >
        <app-icon :name="prependInnerIcon" size="xs" color="gray-light"/>
      </span>
    </div>
  </label>
</template>

<script>
import Icon from '../Icon/Icon.vue'
export default {
  name: 'AppFormInput',
  status: 'prototype',
  release: '1.0.0',
  inheritAttrs: false,
  components: { Icon },
  props: {
    value: {
      required: true
    },
    maxlength: {
      type: String,
      default: '750'
    },
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    secondary: {
      type: Boolean,
      default: false
    },
    borderDashed: {
      type: Boolean,
      default: false
    },
    borderSolid: {
      type: Boolean,
      default: false
    },
    borderBottom: {
      type: Boolean,
      default: false
    },
    boxShadow: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    round: {
      type: Boolean,
      default: false
    },
    highlighted: {
    type: Boolean,
    default: false,
    },
    smallPadding: {
      type: Boolean,
      default: false,
    },
    appendInnerIcon: {
      type: String,
      default: null
    },
    prependInnerIcon: {
      type: String,
      default: null
    },
    colorText: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      lazyValue: this.value
    }
  },
  computed: {
    classesWrapperInput () {
      return {
        'text-field': true,
        'text-field--secondary': this.secondary,
        'text-field--inline': this.inline,
        'text-field--round': this.round,
        'text-field--is-label-active': this.isLabelActive,
        'text-field--is-dirty': this.isDirty,
        'text-field--is-disabled': this.disabled,
        'text-field--is-readonly': this.readonly,
        'text-field--light': this.light
      }
    },
    classesInput () {
      return {
        'text-field__input': 'text-field__input',
        'text-field__input--border-dashed': this.borderDashed,
        'text-field__input--border-solid': this.borderSolid,
        'text-field__input--border-bottom': this.borderBottom,
        'text-field__input--box-shadow': this.boxShadow,
        'text-field__input--highlighted': this.highlighted,
        'text-field__input--small-padding': this.smallPadding,
      };
    },
    internalValue: {
      get () {
        return this.lazyValue
      },
      set (val) {
        this.lazyValue = val
        this.$emit('input', val)
      }
    },
    isDirty () {
      return !!this.lazyValue
    },
    isDisabled () {
      return !!this.disabled || !!this.readonly
    },
    isLabelActive () {
      return this.isDirty
    }
  },
  watch: {
    value (val) {
      this.lazyValue = val
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../styles/styles.scss";
  .text-field {
    &--light {
    .text-field__label {
      color: $color-white;
      font-size: $size-xs;
    }
    .text-field__input {
      input {
        font-weight: normal;
        font-size: $size-xs;
      }
    }
  }
  &__label {
    display: inline-block;
    font-family: $font-text;
    font-size: $size-m;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.22;
    letter-spacing: normal;
    color: $color-purple-darker;
  }
  &__input {
    position: relative;
    width: 100%;
    box-sizing: border-box;
    background-color: $color-input-bg;
    &--border-dashed {
      border: dashed 2px $color-gray-light;
    }
    &--border-solid {
      border: solid 2px $color-gray-light;
    }
    &--border-bottom {
      border-bottom: 1px solid $color-gray;
    }
    &--box-shadow {
      box-shadow: $shadow-m;
    }
    &--highlighted {
      border-radius: 6px;
      box-shadow: 0 0 8px 0 rgba(31, 140, 241, 0.5);
      border: solid 1px rgb(31, 140, 241);
    }
    &--small-padding {
      input {
        padding: 10px !important;
      }
    }
    input {
      display: block;
      width: 100%;
      height: 100%;
      padding: 10px 0;
      border: 0;
      outline: 0;
      font-family: $font-text;
      font-size: 20px;
      font-weight: 600;
      color: $color-purple-dark;
      background: transparent;
      &::-webkit-input-placeholder {
        color: $color-gray-light;
      }
      &::-moz-placeholder {
        color: $color-gray-light;
      }
      &:-ms-input-placeholder {
        color: $color-gray-light;
      }
      &:-moz-placeholder {
        color: $color-gray-light;
      }
    }
    &__icon {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      &--append-inner {
        left: 10px;
      }
      &--prepend-inner {
        right: 10px;
      }
    }
  }
  &--secondary {
    .text-field__label {
      font-size: $size-xs;
      margin-bottom: 5px;
    }
    .text-field__input {
      border-radius: 6px;
      background-color: $color-white;
      input {
        padding: 15px;
        font-size: $size-xs;
        font-weight: normal;
      }
    }
  }
  &--inline {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-around;
    align-items: center;
    .text-field__label {
      flex: 0 0 auto;
      padding-right: 15px;
    }
    .text-field__input {
      flex: auto;
    }
  }
  &--round {
    .text-field__input {
      border-radius: 23px;
      background-color: $color-white;
      input {
        padding: 12px 20px;
        font-size: $size-m;
      }
    }
  }
}
.user-click {
  -webkit-user-select: text;
  -khtml-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;
  user-select: text;
}
</style>
